/** Victim selection (fixate > taunt > threat with overtake rules) and enemy ability target rules. */
import type { EnemyAbility, SimContext, Unit, UnitId, Vec2 } from '../contracts';
import { walkThreatList } from '../combat/threat';

export const isHostileTo = (npc: Unit, u: Unit | undefined): u is Unit => !!u && u.alive && u.faction !== npc.faction;

/** Alive hostile units on the NPC's threat table, in deterministic unitOrder. */
export function threatList(ctx: SimContext, npc: Unit): Unit[] {
  const entries = npc.threat?.entries;
  if (!entries) return [];
  const out: Unit[] = [];
  for (const id of ctx.world.unitOrder) {
    if (!entries[id]) continue;
    const u = ctx.world.units[id];
    if (isHostileTo(npc, u)) out.push(u);
  }
  return out;
}

/** Drop threat entries for units that are gone, dead or no longer hostile. */
export function pruneThreat(ctx: SimContext, npc: Unit): void {
  const t = npc.threat;
  if (!t) return;
  for (const id of Object.keys(t.entries)) if (!isHostileTo(npc, ctx.world.units[id])) delete t.entries[id];
  if (t.victimId && !t.entries[t.victimId]) t.victimId = null;
  if (t.taunt && (t.taunt.until <= ctx.now || !isHostileTo(npc, ctx.world.units[t.taunt.by]))) t.taunt = null;
  if (t.fixate && (t.fixate.until <= ctx.now || !isHostileTo(npc, ctx.world.units[t.fixate.on]))) t.fixate = null;
}

export function ensureThreatEntry(ctx: SimContext, npc: Unit, unitId: UnitId): void {
  const t = npc.threat;
  if (t && !t.entries[unitId]) t.entries[unitId] = { threat: 0, lastAt: ctx.now };
}

/** TrinityCore ReselectVictim: fixate > taunt > top threat, a challenger needs 110% (melee) / 130% (ranged). */
export function pickVictim(ctx: SimContext, npc: Unit): UnitId | null {
  const t = npc.threat;
  if (!t) return null;
  pruneThreat(ctx, npc);
  let victim: UnitId | null = null;
  if (t.fixate) victim = t.fixate.on;
  else if (t.taunt) victim = t.taunt.by;
  else victim = walkThreatList(ctx, npc, (id) => isHostileTo(npc, ctx.world.units[id]));
  t.victimId = victim;
  npc.targetId = victim;
  return victim;
}

export interface AbilityTarget {
  targetId: UnitId | null;
  point: Vec2 | null;
}

/** Resolve an EnemyAbility's target rule. Returns null when nothing valid exists right now. */
export function resolveAbilityTarget(ctx: SimContext, npc: Unit, ab: EnemyAbility): AbilityTarget | null {
  const w = ctx.world;
  const sp = ctx.sys.spatial;
  const victim = npc.threat?.victimId ? w.units[npc.threat.victimId] : undefined;
  const hostiles = (): Unit[] => {
    const list = threatList(ctx, npc);
    if (list.length > 0) return list;
    return w.partyIds.map((id) => w.units[id]).filter((u): u is Unit => isHostileTo(npc, u));
  };
  const range = ab.range ?? 0;
  const inRange = (u: Unit): boolean => (range > 0 ? sp.inRange(w, npc.id, u.id, range) : true);
  const preferInRange = (list: Unit[]): Unit[] => {
    const r = list.filter(inRange);
    return r.length > 0 ? r : list;
  };
  const byRole = (role: 'tank' | 'healer'): Unit | undefined => hostiles().find((u) => u.role === role);

  let target: Unit | undefined;
  switch (ab.target) {
    case 'victim':
      target = victim;
      break;
    case 'tank':
      target = byRole('tank') ?? victim;
      break;
    case 'healer':
      target = byRole('healer') ?? ctx.rng.pick(preferInRange(hostiles()));
      break;
    case 'random':
      target = ctx.rng.pick(preferInRange(hostiles()));
      break;
    case 'randomNotTank': {
      const all = hostiles();
      const notTank = all.filter((u) => u.role !== 'tank' && u.id !== victim?.id);
      target = ctx.rng.pick(preferInRange(notTank.length > 0 ? notTank : all));
      break;
    }
    case 'nearest':
    case 'farthest': {
      const sign = ab.target === 'nearest' ? 1 : -1;
      let best = Infinity;
      for (const u of hostiles()) {
        const d = sign * sp.distance(npc.pos, u.pos);
        if (d < best) {
          best = d;
          target = u;
        }
      }
      break;
    }
    case 'self':
    case 'allies':
      target = npc;
      break;
    case 'lowestHpAlly': {
      let best = Infinity;
      for (const id of w.unitOrder) {
        const u = w.units[id];
        if (!u || !u.alive || u.faction !== npc.faction || u.hp >= u.maxHp) continue;
        if (range > 0 && u.id !== npc.id && !sp.inRange(w, npc.id, u.id, range)) continue;
        const pct = u.hp / u.maxHp;
        if (pct < best) {
          best = pct;
          target = u;
        }
      }
      if (!target) return null; // nobody hurt: hold the heal
      break;
    }
    case 'none':
      return { targetId: null, point: null };
  }
  if (!target) return null;
  const point = ab.targeting === 'ground' ? { x: target.pos.x, y: target.pos.y } : null;
  return { targetId: target.id, point };
}
